<template>
  <div>
    <button
      type="button"
      class="fixed bottom-5 right-5 z-30 flex items-center gap-2 rounded-full bg-base-content text-base-100 shadow-lg px-4 py-3 hover:opacity-90 transition-opacity"
      :aria-label="t('ask_ai')"
      @click="open = !open"
    >
      <sparkle-icon />
      <span class="hidden sm:inline text-sm font-medium">{{ t('ask_ai') }}</span>
    </button>

    <div
      v-if="open"
      class="fixed inset-0 z-40 bg-black/20"
      @click="open = false"
    />

    <aside
      v-if="open"
      class="fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-base-100 shadow-xl flex flex-col border-l border-base-200"
    >
      <header class="flex items-center justify-between px-4 py-3 border-b border-base-200 flex-none">
        <span class="font-semibold flex items-center gap-2">
          <sparkle-icon />
          {{ t('ask_ai') }}
        </span>
        <button
          type="button"
          class="btn btn-ghost btn-sm btn-circle"
          :aria-label="t('ask_ai')"
          @click="open = false"
        >
          ✕
        </button>
      </header>

      <div
        ref="scroll"
        class="flex-1 overflow-y-auto px-4 py-3 space-y-3"
      >
        <p
          v-if="!messages.length"
          class="text-sm text-base-content/60"
        >
          {{ t('ai_assistant_intro') }}
        </p>
        <div
          v-for="(m, i) in messages"
          :key="i"
          :class="m.role === 'user' ? 'text-right' : 'text-left'"
        >
          <div
            class="inline-block rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap text-left max-w-[85%]"
            :class="m.role === 'user' ? 'bg-base-200' : 'bg-base-content/5'"
            dir="auto"
          >
            <span v-if="m.content">{{ m.content }}</span>
            <span
              v-else
              class="opacity-60"
            >{{ t('ai_assistant_thinking') }}</span>
          </div>
        </div>
      </div>

      <form
        class="border-t border-base-200 p-3 flex items-end gap-2 flex-none"
        @submit.prevent="send"
      >
        <textarea
          v-model="draft"
          :placeholder="t('ai_assistant_placeholder')"
          rows="1"
          dir="auto"
          class="base-input flex-1 resize-none max-h-32"
          @keydown.enter.exact.prevent="send"
        />
        <button
          type="submit"
          class="base-button flex-none"
          :disabled="loading || !draft.trim()"
        >
          {{ t('ai_assistant_send') }}
        </button>
      </form>
    </aside>
  </div>
</template>

<script>
import SparkleIcon from './sparkle_icon'

export default {
  name: 'AssistantPanel',
  components: { SparkleIcon },
  props: {
    endpoint: {
      type: String,
      required: true
    },
    authenticityToken: {
      type: String,
      default: ''
    },
    i18n: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      open: false,
      messages: [],
      draft: '',
      loading: false
    }
  },
  watch: {
    open (value) {
      // Fechar o painel cancela um stream em andamento (libera a thread Live no servidor e
      // para de gastar tokens do OpenRouter).
      if (!value) this.abort()
    }
  },
  beforeUnmount () {
    // Navegação (Turbo) desmonta o componente — aborta qualquer stream pendente.
    this.abort()
  },
  methods: {
    t (key) {
      return this.i18n[key] || key
    },
    abort () {
      this.abortController?.abort()
      this.abortController = null
    },
    async send () {
      const text = this.draft.trim()

      if (!text || this.loading) return

      this.messages.push({ role: 'user', content: text })
      this.draft = ''

      // Histórico enviado ao servidor (a cada turno) — antes de adicionar o placeholder vazio.
      const payload = this.messages.map((m) => ({ role: m.role, content: m.content }))

      this.messages.push({ role: 'assistant', content: '' })
      const idx = this.messages.length - 1

      this.loading = true
      this.scrollToBottom()

      const controller = new AbortController()
      this.abortController = controller
      let failed = false

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.authenticityToken
          },
          body: JSON.stringify({ messages: payload }),
          signal: controller.signal
        })

        // Erro/redirect do servidor (CSRF expirado, 429, 401->login HTML...) não é SSE:
        // aborta cedo p/ cair no catch e mostrar erro, em vez de ler HTML como stream.
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        let finished = false

        while (!finished) {
          const { value, done } = await reader.read()

          buffer += decoder.decode(value, { stream: true })

          const lines = buffer.split('\n\n')
          buffer = lines.pop()

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue

            const data = JSON.parse(line.replace(/^data: /, ''))

            if (data.error) {
              failed = true
              this.messages[idx].content = data.error
            } else if (data.delta) {
              this.messages[idx].content += data.delta
            } else if (data.done) {
              // Encerra ao receber o frame de conclusão, não só no EOF físico (evita travar
              // atrás de proxy que mantém a conexão aberta).
              finished = true
            }
          }

          this.scrollToBottom()

          if (done) break
        }
      } catch (error) {
        // Abort proposital (usuário fechou o painel / navegou) não é erro — encerra silencioso.
        if (error.name === 'AbortError') return

        failed = true
        console.error('AI assistant stream error:', error)
      } finally {
        if (this.abortController === controller) this.abortController = null

        // Só mostra o erro genérico quando falhou de verdade e nada chegou (resposta legítima
        // vazia não vira erro).
        if (failed && !this.messages[idx].content) {
          this.messages[idx].content = this.t('ai_assistant_error')
        }

        this.loading = false
        this.scrollToBottom()
      }
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.scroll

        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>
